/**
 * DEMO DATA — supervisor live-listen monitor.
 *
 * ⚠️  PHASE 1 (CLIENT DEMO) ONLY. Real data arrives next phase. See ../demo-mode.ts.
 *
 * Stands in for the supervisor Listen action. It presents as MONITORING A CALL
 * IN PROGRESS, not as playing back a file:
 *
 *   - audio starts immediately on open, no play button to press first;
 *   - it JOINS AT THE CALL'S CURRENT POSITION rather than at 0:00, so a call
 *     that has been running two minutes is heard two minutes in, the way a real
 *     ARI snoop would;
 *   - there is no scrub bar. You cannot rewind a live call, and offering a
 *     seek control is the single thing that would make this read as a recording
 *     player instead of a live monitor.
 *
 * The real path (`POST /supervisor/listen/:callId` → ARI snoop) is implemented
 * and untouched; this is what stands in while there is no live media to snoop.
 */
import { useEffect, useRef, useState, type JSX } from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { RECORDING_BY_CALL_ID, type DemoRecording } from './recordings.js'

/**
 * Simulated call ids are `<recordingUuid>::<seq>` so each arrival is distinct
 * while still pointing at its source audio. Strip the suffix to find the file.
 */
export function recordingForCall(callId: string): DemoRecording | undefined {
  return RECORDING_BY_CALL_ID.get(callId.split('::')[0] ?? callId)
}

function fmt(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00'
  const m = Math.floor(sec / 60)
  const s = String(Math.floor(sec % 60)).padStart(2, '0')
  return `${m}:${s}`
}

export interface DemoCallPlayerProps {
  callId: string | null
  agentName: string
  callerCLI: string
  /** How long the call has been up — where monitoring joins the audio. */
  elapsedSec: number
  onHide: () => void
}

export function DemoCallPlayer({
  callId,
  agentName,
  callerCLI,
  elapsedSec,
  onHide,
}: DemoCallPlayerProps): JSX.Element {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [muted, setMuted] = useState(false)
  const [blocked, setBlocked] = useState(false)
  const [failed, setFailed] = useState(false)
  const [live, setLive] = useState(0)
  const elapsedSecRef = useRef(elapsedSec)
  elapsedSecRef.current = elapsedSec

  const recording = callId ? recordingForCall(callId) : undefined

  /**
   * Begin monitoring: seek to where the call actually is, then play.
   *
   * Held in a ref and invoked from TWO places — the mount effect and the
   * Dialog's `onShow`. That redundancy is deliberate. Whether the <audio> ref
   * is attached before the effect runs depends on React commit order and
   * PrimeReact's portal/transition behaviour, and this feature has already
   * failed once on exactly that assumption. `play()` on an already-playing
   * element is a no-op, so the duplicate call is free; a missed one is silence.
   */
  const startRef = useRef<() => void>(() => undefined)
  startRef.current = (): void => {
    const el = audioRef.current
    if (!el) return
    // Seek against the element's OWN duration, never the manifest estimate.
    // The estimate is derived from file size and a presumed bitrate, and it was
    // wrong once already (64 kbps assumed, 192 kbps actual), which put the
    // playhead past the end of every file — no error, just silence.
    const dur = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : 0
    // Wrap rather than clamp, so a call outlasting its recording keeps playing
    // from the top instead of sitting on a dead final frame.
    el.currentTime = dur ? elapsedSecRef.current % dur : 0
    void el
      .play()
      .then(() => setBlocked(false))
      // Browsers block autoplay until the tab has been interacted with. The
      // click that opened this dialog usually counts, but not always — so
      // surface a tap-to-listen affordance instead of failing silently.
      .catch(() => setBlocked(true))
  }

  // Join the call at its current position and start playing.
  //
  // Depends on `callId` only, NOT on elapsedSec: the caller's elapsed counter
  // advances every second, and re-running this on each tick would restart the
  // audio once a second — silence punctuated by stutter.
  useEffect(() => {
    if (!callId || !recording) return
    const el = audioRef.current
    // The <audio> element is deliberately rendered OUTSIDE <Dialog> (see the
    // render below), so by the time this runs the ref is attached. It used to
    // live inside the dialog, whose content PrimeReact mounts into a portal
    // behind a CSS transition — the ref was still null when this effect ran,
    // the function returned here, and because the deps never changed again it
    // never retried. That is why no audio ever played.
    if (!el) return

    setBlocked(false)
    setFailed(false)
    setLive(elapsedSec)

    const start = (): void => startRef.current()

    // readyState >= 1 (HAVE_METADATA) is the point at which `duration` and
    // seeking are usable. Below that, setting currentTime is discarded.
    if (el.readyState >= 1) start()
    else el.addEventListener('loadedmetadata', start, { once: true })

    return () => {
      el.removeEventListener('loadedmetadata', start)
      el.pause()
    }
    // eslint-disable-next-line
  }, [callId, recording])

  // Local ticker for the on-screen call timer, so it counts up smoothly while
  // the dialog is open rather than only moving when the parent re-renders.
  useEffect(() => {
    if (!callId) return
    const id = setInterval(() => setLive((s) => s + 1), 1000)
    return () => clearInterval(id)
  }, [callId])

  function stopAndClose(): void {
    audioRef.current?.pause()
    onHide()
  }

  function toggleMute(): void {
    const el = audioRef.current
    if (!el) return
    el.muted = !el.muted
    setMuted(el.muted)
  }

  return (
    <>
      {/* OUTSIDE the Dialog on purpose. Dialog content is portalled and mounted
          behind a transition, so an <audio> inside it is not in the DOM when
          the playback effect runs. Out here the element exists for the whole
          lifetime of the page, so the ref is always attached and playback is
          independent of the dialog's animation.
          `loop` keeps a call that outlives its recording producing audio;
          `onError` surfaces a failed fetch/decode, which otherwise presents
          identically to "playing correctly but silent". */}
      {recording && (
        <audio
          ref={audioRef}
          src={recording.file}
          preload="auto"
          loop
          onError={() => setFailed(true)}
        />
      )}
      <Dialog
      visible={callId !== null}
      onHide={stopAndClose}
      onShow={() => startRef.current()}
      header={
        <span className="flex items-center gap-2 text-[12px] font-semibold">
          <span className="kc-pulse inline-block h-2 w-2 bg-rose-500 animate-pulse" />
          Monitoring live call
        </span>
      }
      className="kc-cs-overlay"
      style={{ width: '26rem', maxWidth: '95vw' }}
      dismissableMask
    >
      <div className="flex flex-col gap-3 text-[12px]">
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
          <span className="text-slate-400">Agent</span>
          <span className="font-medium text-slate-800">{agentName}</span>
          <span className="text-slate-400">Caller</span>
          <span className="font-mono text-slate-800">{callerCLI}</span>
          <span className="text-slate-400">Duration</span>
          <span className="tabular-nums font-medium text-slate-800">{fmt(live)}</span>
        </div>

        {(!recording || failed) && (
          <div className="rounded-[3px] border border-amber-200 bg-amber-50 px-2 py-1.5 text-amber-800">
            Audio is not available for this call.
          </div>
        )}

        {recording && (
          <>
            {blocked && (
              <Button
                label="Tap to start listening"
                icon="pi pi-volume-up"
                size="small"
                rounded={false}
                onClick={() => {
                  const el = audioRef.current
                  if (!el) return
                  void el.play().then(() => setBlocked(false)).catch(() => undefined)
                }}
              />
            )}

            {/* A level meter, not a scrub bar — it signals "audio is flowing"
                without implying you can move around inside a live call. */}
            {!blocked && (
              <div className="flex items-center gap-2">
                <span className="flex h-6 flex-1 items-end gap-[2px]">
                  {Array.from({ length: 28 }, (_, i) => (
                    <span
                      key={i}
                      className={`flex-1 rounded-[1px] ${muted ? 'bg-slate-200' : 'bg-emerald-400'}`}
                      style={{
                        height: muted
                          ? '15%'
                          : `${20 + 70 * Math.abs(Math.sin((live + i) * 0.7))}%`,
                        transition: 'height .25s ease',
                      }}
                    />
                  ))}
                </span>
                <Button
                  icon={muted ? 'pi pi-volume-off' : 'pi pi-volume-up'}
                  size="small"
                  text
                  rounded={false}
                  aria-label={muted ? 'Unmute' : 'Mute'}
                  tooltip={muted ? 'Unmute' : 'Mute'}
                  tooltipOptions={{ position: 'top', className: 'kc-cs-tip' }}
                  onClick={toggleMute}
                />
              </div>
            )}

            <Button
              label="Stop listening"
              icon="pi pi-times"
              severity="danger"
              outlined
              size="small"
              rounded={false}
              onClick={stopAndClose}
            />
          </>
        )}
      </div>
      </Dialog>
    </>
  )
}
